import { CommonModule } from '@angular/common';
import { Component, effect, OnDestroy } from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { Subject, takeUntil } from 'rxjs';
import { PrintDocumentService } from '../../../../print-documents/print-document.service';
import { BarChartComponent } from '../../../../shared/components/bar-chart/bar-chart.component';
import { PrintFilterDataComponent } from '../../../../shared/components/print-filter-data/print-filter-data.component';
import {
  DocumentTypeEnum,
  ExportToPdfService,
} from '../../../../shared/services/export-to-pdf.service';
import { LoadingService } from '../../../../shared/services/loading.service';
import { ReportFilterService } from '../../../../shared/services/report-filter.service';
import { ShiftReportService } from '../../../../shared/services/reporting-services/shift-report.service';
import { IReportFilter } from '../../../../utils/interfaces/report-filter';
import { IShiftComparativeReport } from '../../../../utils/interfaces/shift-comparative-report';

@Component({
  selector: 'app-shift',
  standalone: true,
  imports: [CommonModule, PrintFilterDataComponent, BarChartComponent],
  templateUrl: './shift.component.html',
  styleUrl: './shift.component.scss',
})
export class ShiftComponent implements OnDestroy {
  print = false;
  filter: IReportFilter | null = null;
  private readonly unsubscribe$ = new Subject();
  
  data!: ChartConfiguration<'bar'>['data'];
  shiftReportData: IShiftComparativeReport[] = [];
  
  totalResponses = 0;
  overallSatisfaction = 0;

  constructor(
    private readonly reportFilterService: ReportFilterService,
    private readonly shiftReportService: ShiftReportService,
    private readonly loadingService: LoadingService,
    private readonly exportToPdfService: ExportToPdfService,
    private readonly printDocumentService: PrintDocumentService
  ) {
    effect(
      () => {
        this.filter = reportFilterService.getFilter();
        this.getReportData();
      },
      {
        allowSignalWrites: true,
      }
    );

    effect(
      () => {
        this.printDocument(exportToPdfService.getDocumentType());
      },
      {
        allowSignalWrites: true,
      }
    );
  }

  getReportData(): void {
    if (!this.filter) return;

    setTimeout(() => {
      this.loadingService.showLoading();
    }, 0);

    this.shiftReportService
      .getShiftComparativeReport(this.filter)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          this.shiftReportData = res.data || [];
          this.prepareChartData();
          this.calculateSummary();
          this.loadingService.closeLoading();
        },
        error: (error) => {
          console.error('Erro ao carregar dados de turno:', error);
          this.shiftReportData = [];
          this.loadingService.closeLoading();
        }
      });
  }

  private prepareChartData(): void {
    if (!this.shiftReportData.length) return;

    const allShifts = new Set<string>();
    
    if (this.filter?.avaiableShifts && this.filter.avaiableShifts.length > 0) {
      this.shiftReportData.forEach(branch => {
        branch.shiftData.forEach(shift => {
          if (this.filter?.avaiableShifts?.includes(shift.shiftId)) {
            allShifts.add(shift.shiftDescription);
          }
        });
      });
    } else {
      this.shiftReportData.forEach(branch => {
        branch.shiftData.forEach(shift => {
          allShifts.add(shift.shiftDescription);
        });
      });
    }

    const labels = Array.from(allShifts);
    const datasets = this.shiftReportData.map(branch => ({
      label: branch.branchName,
      data: labels.map(shiftDescription => {
        const shiftData = branch.shiftData.find(s => s.shiftDescription === shiftDescription);
        return shiftData ? shiftData.satisfactionPercentage : 0;
      }),
      responseCount: labels.map(shiftDescription => {
        const shiftData = branch.shiftData.find(s => s.shiftDescription === shiftDescription);
        return shiftData ? shiftData.responseCount : 0;
      }),
      backgroundColor: '#BE0013', 
      borderColor: '#BE0013',
      borderWidth: 1,
    }));

    this.data = {
      labels,
      datasets,
    };
  }

  private calculateSummary(): void {
    if (!this.shiftReportData.length) return;

    if (this.filter?.avaiableShifts && this.filter.avaiableShifts.length > 0) {
      this.totalResponses = this.shiftReportData.reduce(
        (total, branch) => {
          const filteredResponses = branch.shiftData
            .filter(shift => this.filter?.avaiableShifts?.includes(shift.shiftId))
            .reduce((branchTotal, shift) => branchTotal + shift.responseCount, 0);
          return total + filteredResponses;
        }, 
        0
      );

      const totalSatisfiedResponses = this.shiftReportData.reduce(
        (total, branch) => {
          const branchSatisfiedResponses = branch.shiftData
            .filter(shift => this.filter?.avaiableShifts?.includes(shift.shiftId))
            .reduce((branchTotal, shift) => branchTotal + shift.satisfiedResponses, 0);
          return total + branchSatisfiedResponses;
        },
        0
      );

      this.overallSatisfaction = this.totalResponses > 0 
        ? Math.round((totalSatisfiedResponses / this.totalResponses) * 100 * 10) / 10
        : 0;
    } else {
      this.totalResponses = this.shiftReportData.reduce(
        (total, branch) => total + branch.totalResponses, 
        0
      );

      const totalSatisfiedResponses = this.shiftReportData.reduce(
        (total, branch) => {
          const branchSatisfiedResponses = branch.shiftData.reduce(
            (branchTotal, shift) => branchTotal + shift.satisfiedResponses,
            0
          );
          return total + branchSatisfiedResponses;
        },
        0
      );

      this.overallSatisfaction = this.totalResponses > 0 
        ? Math.round((totalSatisfiedResponses / this.totalResponses) * 100 * 10) / 10
        : 0;
    }
  }

  printDocument(document: DocumentTypeEnum | null): void {
    if (!document || !this.data) return;

    this.printDocumentService.printDocument('shift', {
      data: this.data,
      filter: this.filter,
      totalResponses: this.totalResponses,
      overallSatisfaction: this.overallSatisfaction,
    });
    this.exportToPdfService.generatePDF(null);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}